/**
 * Build the URL that sends the user back to the legacy application once the
 * migration wizard is done (account created, linked, or already migrated).
 */

export interface ReturnUrlParams {
  /** Sovra Directory user id, sent as `sovra_user_id` */
  directoryUserId?: string;
  /** User id in the legacy application, sent as `app_user_id` */
  externalUserId?: string;
}

function parseUsableReturnUrl(returnUrl: string): URL | null {
  let url: URL;
  try {
    url = new URL(returnUrl);
  } catch {
    return null;
  }
  if (
    url.protocol !== "https:" &&
    !(import.meta.env.DEV && url.protocol === "http:")
  ) {
    return null;
  }
  return url;
}

/** False when the URL is malformed or not https (http is accepted in development only). */
export function isUsableReturnUrl(returnUrl: string): boolean {
  return parseUsableReturnUrl(returnUrl) !== null;
}

/**
 * Returns null when the URL is not usable (see isUsableReturnUrl), so the
 * caller never navigates to it.
 */
export function buildReturnUrl(
  returnUrl: string,
  params: ReturnUrlParams
): string | null {
  const url = parseUsableReturnUrl(returnUrl);
  if (!url) {
    return null;
  }
  if (params.directoryUserId) {
    url.searchParams.set("sovra_user_id", params.directoryUserId);
  }
  if (params.externalUserId) {
    url.searchParams.set("app_user_id", params.externalUserId);
  }
  return url.toString();
}
